/**
 * Mirror types for `data-mapper-bulk-export` (packages/api/src/services/data-mapper/
 * data-mapper-bulk-export/data-mapper-bulk-export.ts and data-plane/bulk-export.ts).
 * Hand-kept in step with the API, like the rest of dataMapper.types.ts.
 */

/** Load batches, dependencies first (the API's module-maps/types.ts). */
export const BULK_EXPORT_BATCH_LABELS: Record<number, string> = {
  1: "Reference data",
  2: "Organisation & parties",
  3: "Catalog & master data",
  4: "Project & order headers",
  5: "Order lines",
  6: "Operations",
  7: "Financial transactions",
  8: "Payroll & history",
};

export interface BulkExportPlanEntry {
  titanTable: string;
  module: string;
  service: string;
  subDataType?: string;
  batch: number;
  outputFile: string;
  sourceColumnCount: number;
  targetFieldCount: number;
  schemaFound: boolean;
  columnsFound: boolean;
  /** true / false with a source connected; null when none is. */
  inSource: boolean | null;
  notes?: string;
}

export interface BulkExportTitanDetection {
  detected: boolean;
  matched: number;
  total: number;
}

export interface BulkExportPlanAnswer {
  batches: Record<string, BulkExportPlanEntry[]>;
  summary: { total: number; schemasFound: number; columnsFound: number; inSource: number | null };
  source: { connected: boolean; sourceType: string | null; detection: BulkExportTitanDetection };
}

export interface BulkExportMappingResult {
  titanTable: string;
  service: string;
  module: string;
  outputFile: string;
  mappingCount: number;
  unmappedTargetFields: string[];
  unmappedSourceColumns: string[];
  error?: string;
}

export interface BulkExportTableResult {
  titanTable: string;
  service: string;
  module: string;
  outputFile: string;
  totalRows: number;
  exportedRows: number;
  validRows: number;
  invalidRows: number;
  errors: string[];
  errorCount: number;
}

export type BulkExportStatus = "running" | "completed" | "completed-with-errors" | "failed" | "cancelled";
export type BulkExportPhase = "exporting" | "packaging" | "done";
export type BulkExportDownloadState =
  | "none"
  | "pending"
  | "ready"
  | "downloaded"
  | "expired"
  | "discarded"
  /** HQ's deletion of it failed; the API retries on every sweep. */
  | "deleting"
  /** Run through the person's own Data Mapper connector: the zip was saved on their machine. */
  | "saved";

export interface BulkExportDownload {
  state: BulkExportDownloadState;
  fileName: string | null;
  bytes: number | null;
  expiresAt: string | null;
  /** State "saved" only: where the zip is on the person's machine. Absent from an older API. */
  savedTo?: string | null;
}

export interface BulkExportSummary {
  tables: number;
  totalRows: number;
  exportedRows: number;
  invalidRows: number;
  errorTables: number;
}

/** `get` / `remove`, and (in part) the final events. */
export interface BulkExportView {
  exportId: string;
  tabId: string;
  dryRun: boolean;
  status: BulkExportStatus;
  phase: BulkExportPhase;
  tableIndex: number;
  tableCount: number;
  currentTable: string | null;
  rows: number;
  totalRows: number;
  startedAt: string;
  finishedAt: string | null;
  error: { reason: string; message: string } | null;
  results: BulkExportTableResult[];
  summary: BulkExportSummary;
  download: BulkExportDownload;
}

/** The progress / complete / error event payload: counts and table names only, never a row value. */
export interface BulkExportEvent {
  exportId: string;
  tabId: string;
  status: BulkExportStatus;
  phase: BulkExportPhase;
  dryRun: boolean;
  tableIndex: number;
  tableCount: number;
  table: string | null;
  outputFile: string | null;
  rows: number;
  totalRows: number;
  summary?: BulkExportSummary;
  download?: BulkExportDownload;
  error?: { reason: string; message: string } | null;
}

/** The events, on service path `data-mapper-bulk-export`, to the requesting person's sockets only. */
export const BULK_EXPORT_EVENTS = {
  progress: "bulk-export-progress",
  complete: "bulk-export-complete",
  error: "bulk-export-error",
} as const;

export type BulkExportEventName = (typeof BULK_EXPORT_EVENTS)[keyof typeof BULK_EXPORT_EVENTS];

export const isFinishedStatus = (status: BulkExportStatus | null): boolean =>
  status !== null && status !== "running";
